import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createSubtask, getSubtasks, updateTask, type Task } from '../../../packages/api-client/pm-api';
import { formatDueDate, parseDueDate } from '../../../utils/date';
import { priorityColor } from '../../../utils/statusColors';
import { ArrowUp, ListTree, Calendar, ChevronRight, X } from 'lucide-react';

// ─── Parent breadcrumb ────────────────────────────────────────────────────────

export function ParentBreadcrumb({ parentTask }: { parentTask: Pick<Task, 'id' | 'title'> }) {
    const navigate = useNavigate();
    return (
        <button type="button" className="task-parent-link" onClick={() => navigate(`/tasks/${parentTask.id}`)} title="Open parent task">
            <ArrowUp size={14} aria-hidden /> Subtask of <span>{parentTask.title}</span>
        </button>
    );
}

// ─── Subtask List ─────────────────────────────────────────────────────────────

// SUBTASK_PRIORITY_COLORS removed — use priorityColor() from statusColors.ts instead.

/** canCreate gates "+ Add" (tasks:create); canUpdate gates the done checkbox (tasks:update). */
export function SubtaskList({ taskId, canCreate = true, canUpdate = true }: { taskId: string; canCreate?: boolean; canUpdate?: boolean }) {
    const navigate = useNavigate();
    const [subtasks, setSubtasks] = useState<Task[]>([]);
    const [newTitle, setNewTitle] = useState('');
    const [adding, setAdding] = useState(false);

    useEffect(() => { getSubtasks(taskId).then(setSubtasks); }, [taskId]);

    async function handleAdd() {
        if (!newTitle.trim()) return;
        const t = await createSubtask(taskId, { title: newTitle, priority: 'none' });
        setSubtasks(prev => [...prev, t]);
        setNewTitle('');
        setAdding(false);
    }

    async function handleToggle(sub: Task) {
        const newStatus = sub.status === 'done' ? 'todo' : 'done';
        await updateTask(sub.id, { status: newStatus });
        setSubtasks(prev => prev.map(s => s.id === sub.id ? { ...s, status: newStatus } : s));
    }

    const done = subtasks.filter(s => s.status === 'done').length;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--space-3)' }}>
                <span className="task-muted task-inline"><ListTree size={16} aria-hidden /> {subtasks.length === 0 ? 'No subtasks.' : `${done} of ${subtasks.length} done`}</span>
                {canCreate && <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>+ Add subtask</button>}
            </div>
            {subtasks.length > 0 && (
                <div style={{ marginBottom: 'var(--space-2)', height: 4, background: 'var(--color-border)', borderRadius: 99 }}>
                    <div style={{ height: '100%', width: `${subtasks.length ? (done / subtasks.length) * 100 : 0}%`, background: 'var(--color-success)', borderRadius: 99, transition: 'width 0.3s' }} />
                </div>
            )}
            <div className="subtask-list">
                {subtasks.map(sub => {
                    // The whole row is clickable to open the subtask's own
                    // detail page. The checkbox stops propagation so a quick
                    // "mark done" doesn't navigate. Priority dot and due date
                    // surface enough context that the user knows what they're
                    // about to open before clicking through.
                    const due = sub.due_date ? parseDueDate(sub.due_date) : null;
                    const overdue = !!due && due < new Date() && sub.status !== 'done';
                    return (
                        <div
                            key={sub.id}
                            className={`subtask-item${sub.status === 'done' ? ' done' : ''}`}
                            onClick={() => navigate(`/tasks/${sub.id}`)}
                            role="link"
                            tabIndex={0}
                            onKeyDown={e => { if (e.key === 'Enter') navigate(`/tasks/${sub.id}`); }}
                            style={{ cursor: 'pointer' }}
                            title="Open subtask"
                        >
                            {canUpdate && <input
                                type="checkbox"
                                aria-label={`Mark "${sub.title}" done`}
                                checked={sub.status === 'done'}
                                onClick={e => e.stopPropagation()}
                                onChange={e => { e.stopPropagation(); handleToggle(sub); }}
                            />}
                            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub.title}</span>
                            {sub.priority && sub.priority !== 'none' && (
                                <span
                                    style={{ width: 8, height: 8, borderRadius: '50%', background: priorityColor(sub.priority) ?? 'var(--ink-muted)', flexShrink: 0 }}
                                    title={`Priority: ${sub.priority}`}
                                />
                            )}
                            {sub.due_date && (
                                <span
                                    style={{ fontSize: 'var(--font-size-xs)', color: overdue ? 'var(--color-danger)' : 'var(--color-text-muted)', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }}
                                    title={overdue ? 'Overdue' : 'Due date'}
                                >
                                    <Calendar size={12} aria-hidden /> {formatDueDate(sub.due_date)}
                                </span>
                            )}
                            <ChevronRight size={14} aria-hidden style={{ color: 'var(--color-text-faint)', flexShrink: 0 }} />
                        </div>
                    );
                })}
                {adding && canCreate && (
                    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                        <input className="form-input" style={{ flex: 1, height: '2rem' }} placeholder="Subtask title…"
                            autoFocus value={newTitle} onChange={e => setNewTitle(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleAdd(); if (e.key === 'Escape') setAdding(false); }} />
                        <button className="btn btn-primary btn-sm" onClick={handleAdd}>Add</button>
                        <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)} aria-label="Cancel"><X size={16} /></button>
                    </div>
                )}
            </div>
        </div>
    );
}
